import { useEffect, useRef, useState } from 'react';
import { DeadForm, RecoveryPath, ScreenId, SeasoningForm } from '../types/withdraw';
import { addDaysISO, todayISO } from '../utils/dates';
import { isDeadValid, isSeasoningValid } from '../utils/withdrawValidation';

function initialDead(): DeadForm {
  return {
    reason: '',
    withdrawDate: todayISO(),
    notes: '',
    lenderVariant: 'sync',
    withdrawnWithLender: false,
    lenderWithdrawDate: '',
    reApp: false,
    newLender: '',
    carryDocs: [],
  };
}

function initialSeasoning(): SeasoningForm {
  return {
    reason: '',
    currentRate: '',
    targetRate: '',
    currentScore: '',
    targetScore: '',
    expectedEndDate: '',
    seasoningType: '',
    eligibleDate: '',
    cadence: 'biweekly',
    suspendedUntil: addDaysISO(90),
  };
}

export function stepForScreen(screen: ScreenId): number {
  if (screen === 'path') return 0;
  if (screen === 'success') return 2;
  return 1;
}

export function useWithdrawFlow() {
  const [screen, setScreen] = useState<ScreenId>('path');
  const [path, setPath] = useState<RecoveryPath | null>(null);
  const [dead, setDead] = useState<DeadForm>(initialDead);
  const [seasoning, setSeasoning] = useState<SeasoningForm>(initialSeasoning);
  const [submitting, setSubmitting] = useState(false);
  const timer = useRef<number | null>(null);

  const step = stepForScreen(screen);
  const prevStep = useRef(step);
  const direction = step >= prevStep.current ? 1 : -1;
  useEffect(() => {
    prevStep.current = step;
  }, [step]);

  useEffect(() => () => {
    if (timer.current) window.clearTimeout(timer.current);
  }, []);

  const canContinue =
    step === 0
      ? path !== null
      : path === 'dead'
        ? isDeadValid(dead)
        : isSeasoningValid(seasoning);

  // Jump straight to any screen (used by the prototype screen switcher).
  const goTo = (next: ScreenId) => {
    if (timer.current) window.clearTimeout(timer.current);
    setSubmitting(false);
    if (next === 'dead' || next === 'seasoning') setPath(next);
    if (next === 'success') {
      const p = path ?? 'dead';
      setPath(p);
      if (p === 'dead' && !isDeadValid(dead)) {
        setDead((d) => ({
          ...d,
          reason: d.reason || 'Borrower Backed Out',
          notes: d.notes || 'Borrower is pausing their home search until spring (sample note).',
          withdrawnWithLender: true,
          lenderWithdrawDate: d.lenderWithdrawDate || todayISO(),
        }));
      }
      if (p === 'seasoning' && !isSeasoningValid(seasoning)) {
        setSeasoning((s) => ({ ...s, reason: 'Rate Watch', currentRate: '7.125', targetRate: '6.250' }));
      }
    }
    setScreen(next);
  };

  const next = () => {
    if (!canContinue || submitting) return;
    if (step === 0 && path) {
      setScreen(path);
      return;
    }
    setSubmitting(true);
    timer.current = window.setTimeout(() => {
      setSubmitting(false);
      setScreen('success');
    }, 750);
  };

  const back = () => {
    if (step === 1 && !submitting) setScreen('path');
  };

  const reset = () => {
    if (timer.current) window.clearTimeout(timer.current);
    setSubmitting(false);
    setPath(null);
    setDead(initialDead());
    setSeasoning(initialSeasoning());
    setScreen('path');
  };

  const updateDead = (patch: Partial<DeadForm>) => setDead((d) => ({ ...d, ...patch }));
  const updateSeasoning = (patch: Partial<SeasoningForm>) => setSeasoning((s) => ({ ...s, ...patch }));

  return {
    screen,
    step,
    direction,
    path,
    setPath,
    dead,
    seasoning,
    updateDead,
    updateSeasoning,
    canContinue,
    submitting,
    goTo,
    next,
    back,
    reset,
  };
}

export type WithdrawFlow = ReturnType<typeof useWithdrawFlow>;
